import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { ReactNode } from 'react';
import ExecutionContext from '../../ExecutionContext';
import type { SearchResultResponse } from '../../Search/SearchResultResponse.csharp';
import { FormattedText } from '../../Txt/TxtBase';
import { RequestHelper } from '../RequestHelper/RequestHelper';
import type { QueryParameter } from '~/Partials/SearchBase/QueryParameterUtils';
import { buildAbsoluteUrlWithQueryParameters, formatQueryParameters } from '~/Partials/SearchBase/QueryParameterUtils';
import { InfiniteScroll } from './InfiniteScroll';
import styles from './PagedFetchData.module.css';
import type {
    BasePaginationStatusTranslations,
    PaginationStatusWithRetryTranslations,
} from './PaginationStatus/PaginationStatus';
import { PaginationStatus } from './PaginationStatus/PaginationStatus';

interface PagedFetchDataTranslations {
    showingFromTo: string;
    noResultsFound: string;
    showingNumber?: string;
    showingOf?: string;
}

interface BasePagedFetchDataProps<ElementsType> {
    baseUrl: string;
    take: number;
    searchParams?: QueryParameter[];
    debounceTime?: number;
    initSkip?: number;
    autoRun?: boolean;
    initialData?: SearchResultResponse<ElementsType>;
    children?: (params: { data: ElementsType }) => ReactNode;
    allDataRender?: (params: { data: ElementsType[] }) => ReactNode;
    onTotalCountChanged?: (totalCount: number) => void;
    listClassName?: string;
}

interface PagedFetchDataWithInfiniteLoadProps<ElementsType> extends BasePagedFetchDataProps<ElementsType> {
    translations: PagedFetchDataTranslations & PaginationStatusWithRetryTranslations;
    loadOnScroll: true;
}

interface PagedFetchDataWithManualLoadProps<ElementsType> extends BasePagedFetchDataProps<ElementsType> {
    translations: PagedFetchDataTranslations & BasePaginationStatusTranslations;
    loadOnScroll?: false;
}

export type PagedFetchDataProps<ElementsType> =
    | PagedFetchDataWithInfiniteLoadProps<ElementsType>
    | PagedFetchDataWithManualLoadProps<ElementsType>;

interface PagedFetchDataState<ElementsType> {
    data: ElementsType[];
    skip: number;
    loading: boolean;
    totalCount?: number;
    error?: string;
}

export class PagedFetchData<
    ResponseType extends SearchResultResponse<ElementsType>,
    ElementsType,
> extends React.PureComponent<PagedFetchDataProps<ElementsType>, PagedFetchDataState<ElementsType>> {
    newDataSeparatorId: string;

    static defaultProps = {
        initSkip: 0,
    };

    constructor(props: PagedFetchDataProps<ElementsType>) {
        super(props);

        const initialItems = props.initialData?.items || [];
        const totalCount = props.initialData?.totalCount;

        this.state = {
            data: initialItems,
            skip: 0,
            totalCount,
            loading: false,
            error: undefined,
        };

        this.newDataSeparatorId = uniqueId('NewDataSeparator__');
    }

    componentDidUpdate = (oldProps: PagedFetchDataProps<ElementsType>) => {
        const { searchParams, initSkip = 0 } = this.props;

        const haveParamsChanged = () => {
            if (oldProps.searchParams === undefined && searchParams === undefined) {
                return false;
            }

            if (oldProps.searchParams === undefined || searchParams === undefined) {
                return true;
            }

            return formatQueryParameters(oldProps.searchParams) !== formatQueryParameters(searchParams);
        };

        if (haveParamsChanged()) {
            this.setState({ data: [], skip: initSkip });
        }
    };

    handleLoadNewItems = () => {
        const { initSkip = 0 } = this.props;
        const { data } = this.state;

        this.setState({ skip: initSkip + data.length, error: undefined });
    };

    getSearchQueryString = () => {
        const { baseUrl, take, searchParams = [] } = this.props;
        const { skip, error } = this.state;

        return error
            ? ''
            : buildAbsoluteUrlWithQueryParameters(baseUrl, [
                  ...searchParams,
                  { name: 'skip', value: skip.toString() },
                  { name: 'take', value: take.toString() },
              ]);
    };

    handleLoadNewData = (newData: ResponseType) => {
        const { onTotalCountChanged } = this.props;
        const { data } = this.state;
        this.setState({ data: data.concat(newData.items), totalCount: newData.totalCount });

        if (!ExecutionContext.IsServerSide()) {
            const newDataSeparator = document.getElementById(this.newDataSeparatorId);

            if (newDataSeparator) {
                newDataSeparator.focus({ preventScroll: true });
            }
        }

        !!onTotalCountChanged && onTotalCountChanged(newData.totalCount);
    };

    handleLoadDataError = (error: string) => {
        this.setState({ error, loading: false });
    };

    handleToggleLoading = (loadStatus: boolean) => {
        this.setState({ loading: loadStatus });
    };

    render() {
        const {
            debounceTime,
            children,
            translations,
            autoRun,
            initSkip = 0,
            loadOnScroll,
            allDataRender,
            listClassName,
        } = this.props;
        const { data, totalCount, error, loading, skip } = this.state;
        const dataLength = data.length + initSkip;
        const hasMoreData = (!autoRun && !totalCount) || (!!totalCount && totalCount !== dataLength);
        const hasNoResults = !loading && !error && dataLength === 0;

        const { showingFromTo, showingNumber, showingOf } = translations;

        const separatorIndex = skip - initSkip;

        const renderData = () =>
            data.length > 0 && (
                <>
                    {!!allDataRender && allDataRender({ data })}
                    {!!children && (
                        <ul className={listClassName}>
                            {data.map((item, key) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={`${key}_singleData_parent`}>
                                    {key === separatorIndex && skip !== initSkip && (
                                        <li
                                            id={key === separatorIndex ? this.newDataSeparatorId : undefined}
                                            role="separator"
                                            className="visuallyHidden"
                                            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                            tabIndex={0}
                                        >
                                            {FormattedText(showingFromTo, { from: key + 1, to: data.length })}
                                        </li>
                                    )}
                                    {/* eslint-disable-next-line react/no-array-index-key */}
                                    <li key={key}>{children({ data: item })}</li>
                                </React.Fragment>
                            ))}
                        </ul>
                    )}
                </>
            );

        const hasError = !!error;

        const basePaginationStatusModel = {
            loading,
            hasError,
            hasMoreData,
            loadMoreData: this.handleLoadNewItems,
            hasNoResults,
        };

        return (
            <>
                <RequestHelper<ResponseType>
                    url={this.getSearchQueryString()}
                    debounceTime={debounceTime}
                    autoRun={autoRun}
                    onLoadData={(d: ResponseType) => this.handleLoadNewData(d)}
                    onLoadDataError={(e: string) => this.handleLoadDataError(e)}
                    onToggleLoading={(loadStatus: boolean) => this.handleToggleLoading(loadStatus)}
                />
                {!!data.length && !!totalCount && !!showingNumber && !!showingOf && (
                    <header className={styles.CountInfo}>
                        {`${showingNumber} ${data.length} ${showingOf} ${totalCount}`}
                    </header>
                )}
                {loadOnScroll ? (
                    <InfiniteScroll
                        hasError={hasError}
                        loading={loading}
                        hasMoreData={hasMoreData}
                        loadMoreData={this.handleLoadNewItems}
                        renderData={renderData}
                    >
                        <PaginationStatus {...basePaginationStatusModel} translations={translations} allowRetry />
                    </InfiniteScroll>
                ) : (
                    <>
                        {renderData()}
                        <PaginationStatus {...basePaginationStatusModel} translations={translations} />
                    </>
                )}
            </>
        );
    }
}
